/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box } from "@mui/material";
import { DAILY_LOGBOOK_COLORS, negativeCellStyles, positiveVarianceStyles } from "./styles";

export const EMPTY_DISPLAY = "--";

/**
 * A plain metric figure, tinted only when the API marks it negative.
 */
export const renderMetricCell = (cell) => {
  if (!cell) return EMPTY_DISPLAY;
  if (!cell.negative) return cell.display ?? EMPTY_DISPLAY;

  return (
    <Box component="span" data-negative="true" sx={negativeCellStyles}>
      {cell.display}
    </Box>
  );
};

/**
 * A variance figure, tinted up or down. A missing variance stays neutral rather than
 * reading as a decline.
 */
export const renderVarianceCell = (cell) => {
  if (!cell || cell.value === null || cell.value === undefined) {
    return cell?.display ?? EMPTY_DISPLAY;
  }

  return (
    <Box
      component="span"
      data-negative={cell.negative ? "true" : undefined}
      data-variance={cell.negative ? "down" : "up"}
      sx={cell.negative ? negativeCellStyles : positiveVarianceStyles}
    >
      {cell.display}
    </Box>
  );
};

export const varianceColor = (cell) => {
  if (!cell || cell.value === null || cell.value === undefined) return "#a1a1aa";
  return cell.negative ? DAILY_LOGBOOK_COLORS.negative : DAILY_LOGBOOK_COLORS.positive;
};

/**
 * One comparison line under a metric's headline figure: the window's own value on the
 * left and its variance against today on the right.
 */
export const ComparisonRow = ({ label, cell, varianceCell, testId }) => (
  <Box
    data-testid={testId}
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: "8px",
      fontSize: "12px",
      color: "#71717a",
      marginTop: "2px",
      minWidth: 0,
    }}
  >
    <Box component="span" sx={{ flexShrink: 0 }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{
        flex: "0 1 auto",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {cell?.display ?? EMPTY_DISPLAY}
    </Box>
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        fontWeight: 600,
        minWidth: "48px",
        textAlign: "right",
        color: varianceColor(varianceCell),
      }}
    >
      {varianceCell?.display ?? EMPTY_DISPLAY}
    </Box>
  </Box>
);
